import { useState } from "react";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/common/lib/api";
import { bridgeStatusQueryKey } from "@/common/lib/api/bridge";
import { useAccountContext } from "@providers/AccountProvider";
import ErrorBox from "@/common/components/ErrorBox";
import CheckCircleIcon from "@icons/check-circle.svg?react";
import { isValidEzyvetBaseUrl, normalizeEzyvetBaseUrl } from "@/common/utils/ezyvetBaseUrl";

interface EzyvetConnectionProps {
    /**
     * The currently-saved address (`BridgeStatus.ezyvet_base_url`), pre-filling the input so this
     * reads as "here's what's saved, edit to change it" rather than always rendering empty with no
     * way to tell whether anything was ever actually saved.
     */
    currentBaseUrl?: string | null;
}

/**
 * EzyvetConnection
 *
 * Unlike Cornerstone/AVImark, whose bridge auto-detects its local DB connection and self-reports
 * it, there is no local artifact on a clinic's machine that reveals a cloud SaaS URL — a human has
 * to type the clinic's ezyVet address in here. Shown only once the ezyVet bridge module itself is
 * installed (see `BridgeDownload`'s gating): `POST bridge/ezyvet-connection` 404s otherwise, since
 * there is no active ezyVet `bridge_installations` row to attach the address to yet.
 *
 * Saving does not confirm the address is correct — only that it's a well-formed ezyVet URL. The
 * bridge's own "Sign in to ezyVet" flow is what actually proves it (a real sign-in attempt against
 * the clinic's ezyVet site).
 */
export default function EzyvetConnection({ currentBaseUrl }: EzyvetConnectionProps) {
    const { accountData } = useAccountContext();
    const queryClient = useQueryClient();
    const [baseUrl, setBaseUrl] = useState(currentBaseUrl ?? "");
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    const trimmedBaseUrl = baseUrl.trim();
    // Only judged once something has actually been typed -- an empty field disables Save for a
    // different, already-handled reason (see the button below), not because it's "invalid".
    const isInvalid = trimmedBaseUrl !== "" && !isValidEzyvetBaseUrl(normalizeEzyvetBaseUrl(trimmedBaseUrl));

    const handleSave = async () => {
        const trimmed = trimmedBaseUrl;
        if (!trimmed || isInvalid) return;

        setIsSaving(true);
        setError(null);
        setSaved(false);

        try {
            await apiFetch("bridge/ezyvet-connection", {
                method: "POST",
                // Normalized here, not just validated against the normalized form (PR review,
                // 2026-10-06): the server already re-normalizes before persisting, but sending the
                // raw trimmed value left that as the ONLY thing guaranteeing a trailing slash ever
                // lands in storage -- a silent cross-repo coupling this call shouldn't depend on.
                json: { base_url: normalizeEzyvetBaseUrl(trimmed) },
                timeout: 15_000,
            });
            // Without this, the shared BridgeStatus cache entry keeps serving the address this just
            // replaced: BridgeDownload's address field reads its initial value from that same cache
            // (`currentEzyvetBaseUrl`), so reselecting ezyVet there would refill the OLD address, and
            // the next Download click would re-save it, undoing what was just saved here (PR review,
            // 2026-10-06).
            await queryClient.invalidateQueries({ queryKey: bridgeStatusQueryKey(accountData?.id) });
            setSaved(true);
        } catch (err: any) {
            Sentry.captureException(err);

            // Duck-typed on `.response`, matching `setupBridgeIntegration` in
            // `@common/lib/api/bridge` — a real ky HTTPError always has this shape, and a network
            // failure or abort (no `.response`) falls through to the generic message below.
            let message = "Failed to save your ezyVet address. Please try again or contact support.";
            const res = (err as { response?: { json: () => Promise<{ error?: string }> } })?.response;
            if (res) {
                try {
                    const body = await res.json();
                    if (body?.error) message = body.error;
                } catch {
                    // Non-JSON error body — keep the generic message.
                }
            }
            setError(message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 flex flex-col gap-3">
            <div>
                <p className="text-sm font-medium text-gray-700">Your clinic&rsquo;s ezyVet address</p>
                <p className="text-xs text-gray-500 mt-1">
                    HappyDoc Bridge needs your clinic&rsquo;s ezyVet web address to sign in and export Health Status
                    records, e.g. https://yourclinic.ezyvet.com
                </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
                <input
                    type="text"
                    value={baseUrl}
                    onChange={(e) => {
                        setBaseUrl(e.target.value);
                        setSaved(false);
                        setError(null);
                    }}
                    placeholder="https://yourclinic.ezyvet.com"
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-max-500"
                />
                <button
                    type="button"
                    className="primary-button"
                    onClick={handleSave}
                    disabled={!trimmedBaseUrl || isInvalid || isSaving}
                >
                    {isSaving ? "Saving…" : "Save"}
                </button>
            </div>

            {isInvalid && !isSaving && (
                <p className="text-xs text-red-600">
                    Must be an https:// URL on ezyvet.com or a *.ezyvet.com subdomain, e.g.
                    https://yourclinic.ezyvet.com
                </p>
            )}

            {saved && !isSaving && (
                <div className="flex items-center gap-2 text-sm text-green-700">
                    <CheckCircleIcon className="h-4 w-4 shrink-0" />
                    <span>Saved. Open the bridge&rsquo;s &ldquo;Sign in to ezyVet&rdquo; menu to connect.</span>
                </div>
            )}

            {error && <ErrorBox msg={error} />}
        </div>
    );
}
